/**
 * Date/Time Utilities
 * 
 * Centralized date and time formatting functions.
 */

/**
 * Format an ISO 8601 date/time string for display
 * @param dateTimeStr ISO date string (e.g., "2025-11-18T13:40:58.333")
 * @returns Formatted date/time string (e.g., "November 18, 2025, 1:40:58 PM") or original if invalid
 */
export function formatDateTime(dateTimeStr: string): string {
    try {
        const date = new Date(dateTimeStr);
        if (isNaN(date.getTime())) {
            return dateTimeStr; // Return original if invalid
        }
        // Format as: "Tuesday, November 18, 2025, 1:40:58 PM"
        return date.toLocaleString('en-US', {
            weekday: 'long',
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
            second: '2-digit',
            hour12: true
        });
    } catch (e) {
        return dateTimeStr; // Return original if parsing fails
    }
}

/**
 * Format a date for display (date only, short month)
 * @param dateTimeStr ISO date string
 * @returns Formatted date string (e.g., "Nov 18, 2025") or original if invalid
 */
export function formatDateShort(dateTimeStr: string): string {
    try {
        return new Date(dateTimeStr).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
    } catch {
        return dateTimeStr;
    }
}

/**
 * Format a time for display (time only, no date)
 * @param dateTimeStr ISO date/time string
 * @returns Formatted time string (e.g., "1:40:58 PM") or original if invalid
 */
export function formatTimeShort(dateTimeStr: string): string {
    try {
        return new Date(dateTimeStr).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
    } catch {
        return dateTimeStr;
    }
}

/**
 * Check if a date is in the past
 * @param dateTimeStr ISO date string
 * @returns true if date is in the past
 */
export function isPast(dateTimeStr: string): boolean {
    try {
        const date = new Date(dateTimeStr);
        return date.getTime() < Date.now();
    } catch {
        return false;
    }
}

/**
 * Check if a date is in the future
 * @param dateTimeStr ISO date string
 * @returns true if date is in the future
 */
export function isFuture(dateTimeStr: string): boolean {
    try {
        const date = new Date(dateTimeStr);
        return date.getTime() > Date.now();
    } catch {
        return false;
    }
}

